import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const STATUS_COLORS = {
  pending:    { bg:'rgba(232,164,58,0.12)',  color:'#e8a43a',  label:'Pending'    },
  processing: { bg:'rgba(78,156,245,0.12)',  color:'#4e9cf5',  label:'Processing' },
  fulfilled:  { bg:'rgba(62,207,142,0.12)',  color:'#3ecf8e',  label:'Fulfilled'  },
  cancelled:  { bg:'rgba(107,114,128,0.12)', color:'#6b7280',  label:'Cancelled'  },
};
const PAY_COLORS = {
  unpaid:     { bg:'rgba(240,96,96,0.12)',   color:'#ef4444',  label:'Unpaid'     },
  paid:       { bg:'rgba(62,207,142,0.12)',  color:'#3ecf8e',  label:'Paid'       },
  refunded:   { bg:'rgba(107,114,128,0.12)', color:'#6b7280',  label:'Refunded'   },
};
const METHOD_ICONS = { mtn_momo:'📱', bank_transfer:'🏦', card:'💳', stripe:'💳' };

function Badge({ value, map }) {
  const s = map[value] || { bg:'rgba(255,255,255,0.06)', color:'var(--text-muted)', label: value };
  return (
    <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background:s.bg, color:s.color, whiteSpace:'nowrap' }}>
      {s.label}
    </span>
  );
}

function DetailModal({ order, onClose, onUpdate }) {
  const [status,        setStatus]        = useState(order.status);
  const [paymentStatus, setPaymentStatus] = useState(order.payment_status);
  const [notes,         setNotes]         = useState(order.notes || '');
  const [saving,        setSaving]        = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orders/${order.id}`, { status, payment_status: paymentStatus, notes });
      onUpdate({ ...order, status, payment_status: paymentStatus, notes });
      onClose();
    } catch (e) { alert(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const sel = { width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-primary)', padding:'9px 12px', fontSize:13, outline:'none', fontFamily:'inherit', cursor:'pointer' };
  const lbl = { fontSize:11, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-muted)', display:'block', marginBottom:6 };

  const items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:600, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:20, width:'100%', maxWidth:580, maxHeight:'90vh', overflow:'auto', animation:'scaleIn 0.2s ease' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Order</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, color:'var(--accent)', fontFamily:'monospace' }}>{order.reference}</div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>{new Date(order.created_at).toLocaleString('en-RW')}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:18, cursor:'pointer', lineHeight:1, padding:4 }}>✕</button>
        </div>

        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:20 }}>

          {/* Customer */}
          <div style={{ background:'var(--bg-elevated)', borderRadius:12, padding:'14px 16px', display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Customer</div>
            {[
              ['Name',    order.full_name],
              ['Email',   order.email],
              ['Phone',   order.phone || '—'],
              ['Address', order.shipping_address || '—'],
            ].map(([k,v]) => (
              <div key={k} style={{ display:'flex', gap:8, fontSize:13 }}>
                <span style={{ color:'var(--text-muted)', minWidth:60 }}>{k}</span>
                <span style={{ color:'var(--text-primary)', fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Items */}
          <div>
            <div style={lbl}>Order Items</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {items.map((item, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)', fontSize:13 }}>
                  <div>
                    <div style={{ fontWeight:500 }}>{item.name}{item.variant ? ` (${item.variant})` : ''}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2, textTransform:'capitalize' }}>{item.type} × {item.qty}</div>
                  </div>
                  <div style={{ fontWeight:700, color:'var(--accent)' }}>{item.currency} {Number(item.price * item.qty).toLocaleString()}</div>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 14px', fontWeight:700, fontSize:14, borderTop:'1px solid var(--border)', marginTop:4 }}>
                <span>Total</span>
                <span style={{ color:'var(--accent)' }}>{order.currency} {Number(order.total_amount).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Payment method */}
          <div style={{ display:'flex', gap:8, alignItems:'center', padding:'10px 14px', background:'var(--bg-elevated)', borderRadius:8, fontSize:13 }}>
            <span style={{ fontSize:18 }}>{METHOD_ICONS[order.payment_method] || '💰'}</span>
            <div>
              <div style={{ fontWeight:600 }}>{order.payment_method?.replace(/_/g,' ')}</div>
              {order.momo_phone && <div style={{ fontSize:11, color:'var(--text-muted)' }}>MoMo: {order.momo_phone}</div>}
            </div>
          </div>

          {/* Update status */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <label style={lbl}>Order Status</label>
              <select value={status} onChange={e=>setStatus(e.target.value)} style={sel}>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label style={lbl}>Payment Status</label>
              <select value={paymentStatus} onChange={e=>setPaymentStatus(e.target.value)} style={sel}>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>

          <div>
            <label style={lbl}>Internal Notes</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3}
              placeholder="Add a note about this order…"
              style={{ ...sel, resize:'vertical', lineHeight:1.6 }}/>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{ flex:1, padding:'11px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', cursor:'pointer', fontFamily:'inherit', fontSize:13 }}>Cancel</button>
            <button onClick={save} disabled={saving}
              style={{ flex:2, padding:'11px', borderRadius:10, border:'none', background:'var(--accent)', color:'#080a0d', fontWeight:700, cursor:'pointer', fontFamily:'inherit', fontSize:13, opacity:saving?0.7:1 }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter,   setFilter]   = useState({ status:'', payment_status:'', payment_method:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status)         params.status         = filter.status;
      if (filter.payment_status) params.payment_status = filter.payment_status;
      if (filter.payment_method) params.payment_method = filter.payment_method;
      const res = await api.get('/admin/orders', { params });
      setOrders(res.data.data || []);
    } catch { setOrders([]); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = (updated) => {
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
  };

  const sel = (k,v) => setFilter(f => ({ ...f, [k]:v }));
  const totalRevenue = orders.filter(o=>o.payment_status==='paid').reduce((a,o)=>a+Number(o.total_amount),0);
  const pendingCount = orders.filter(o=>o.status==='pending').length;
  const paidCount    = orders.filter(o=>o.payment_status==='paid').length;

  const fSel = { padding:'8px 12px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg-elevated)', color:'var(--text-primary)', fontSize:12, outline:'none', cursor:'pointer', fontFamily:'inherit' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:700, marginBottom:4 }}>Orders</h1>
          <div style={{ fontSize:13, color:'var(--text-muted)' }}>Marketplace orders from cart checkout</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:14 }}>
        {[
          { label:'Total Orders',    value:orders.length,                          color:'var(--accent)' },
          { label:'Pending',         value:pendingCount,                           color:'#f0b832' },
          { label:'Paid Orders',     value:paidCount,                              color:'#3ecf8e' },
          { label:'Revenue (Paid)',  value:`RWF ${totalRevenue.toLocaleString()}`, color:'#3ecf8e' },
        ].map(s => (
          <div key={s.label} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>{s.label}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:700, color:s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
        <select value={filter.status} onChange={e=>sel('status',e.target.value)} style={fSel}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={filter.payment_status} onChange={e=>sel('payment_status',e.target.value)} style={fSel}>
          <option value="">All Payments</option>
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
        </select>
        <select value={filter.payment_method} onChange={e=>sel('payment_method',e.target.value)} style={fSel}>
          <option value="">All Methods</option>
          <option value="mtn_momo">MTN MoMo</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="card">Card</option>
        </select>
        <div style={{ fontSize:12, color:'var(--text-muted)', marginLeft:'auto' }}>
          {orders.length} order{orders.length!==1?'s':''}
        </div>
      </div>

      {/* Table */}
      <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:16, overflow:'hidden' }}>
        {/* Table header */}
        <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 120px 140px 120px 100px 80px', gap:0, padding:'10px 20px', borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)' }}>
          {['Reference','Customer','Amount','Payment','Status','Method',''].map(h => (
            <div key={h} style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding:60, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>Loading orders…</div>
        ) : orders.length === 0 ? (
          <div style={{ padding:60, textAlign:'center' }}>
            <div style={{ fontSize:'2.5rem', opacity:0.15, marginBottom:12 }}>◈</div>
            <div style={{ color:'var(--text-muted)', fontSize:13 }}>No orders yet</div>
          </div>
        ) : (
          orders.map((order, i) => {
            const items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
            return (
              <div key={order.id}
                style={{ display:'grid', gridTemplateColumns:'160px 1fr 120px 140px 120px 100px 80px', gap:0, padding:'14px 20px', borderBottom: i < orders.length-1 ? '1px solid var(--border)' : 'none', alignItems:'center', transition:'background 0.15s', cursor:'pointer' }}
                onClick={() => setSelected(order)}
                onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>

                {/* Reference */}
                <div>
                  <div style={{ fontFamily:'monospace', fontSize:12, fontWeight:700, color:'var(--accent)', marginBottom:3 }}>{order.reference}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>{new Date(order.created_at).toLocaleDateString('en-RW')}</div>
                </div>

                {/* Customer */}
                <div>
                  <div style={{ fontSize:13, fontWeight:500, marginBottom:2 }}>{order.full_name}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>{order.email}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
                    {items.length} item{items.length!==1?'s':''}: {items.slice(0,2).map(i=>i.name).join(', ')}{items.length>2?`…`:''}
                  </div>
                </div>

                {/* Amount */}
                <div style={{ fontWeight:700, fontSize:13, color:'var(--accent)' }}>
                  {order.currency} {Number(order.total_amount).toLocaleString()}
                </div>

                {/* Payment status */}
                <div><Badge value={order.payment_status} map={PAY_COLORS}/></div>

                {/* Order status */}
                <div><Badge value={order.status} map={STATUS_COLORS}/></div>

                {/* Method */}
                <div style={{ fontSize:13 }}>
                  {METHOD_ICONS[order.payment_method]||'💰'} <span style={{ fontSize:11, color:'var(--text-muted)', textTransform:'capitalize' }}>{order.payment_method?.replace(/_/g,' ')}</span>
                </div>

                {/* Action */}
                <div>
                  <button onClick={e=>{ e.stopPropagation(); setSelected(order); }}
                    style={{ fontSize:12, padding:'5px 12px', borderRadius:6, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s' }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.color='var(--accent)';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.color='var(--text-secondary)';}}>
                    Open
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {selected && <DetailModal order={selected} onClose={() => setSelected(null)} onUpdate={handleUpdate}/>}

      <style>{`@keyframes scaleIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }`}</style>
    </div>
  );
}
